<template>
  <div class="process-instance-container">
    <div class="search-container">
      <el-form :inline="true" :model="searchForm" size="small">
        <el-form-item label="工单编号">
          <el-input
            v-model="searchForm.order_no"
            placeholder="请输入工单编号"
            clearable
          />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="searchForm.status" placeholder="请选择状态" clearable>
            <el-option label="进行中" :value="1" />
            <el-option label="已完成" :value="2" />
            <el-option label="已终止" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-container">
      <el-table
        v-loading="loading"
        :data="tableData"
        style="width: 100%"
        :cell-style="{ textAlign: 'center' }"
        :header-cell-style="{ textAlign: 'center', backgroundColor: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="instance_no" label="实例编号" width="180" align="center" />
        <el-table-column prop="order_no" label="工单编号" width="180" align="center">
          <template slot-scope="{ row }">
            <el-link type="primary" :underline="false" @click="handleViewOrder(row)">{{ row.order_no }}</el-link>
          </template>
        </el-table-column>
        <el-table-column prop="definition_name" label="流程名称" width="180" align="center">
          <template slot-scope="{ row }">
            <div class="process-info">
              <span>{{ row.definition_name }}</span>
              <div class="process-version">版本: v{{ row.version || '1.0' }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="current_node_name" label="当前节点" width="150" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="getNodeTagType(row.current_node_type)">{{ row.current_node_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="start_user_name" label="发起人" width="120" align="center" />
        <el-table-column prop="duration" label="运行时长" width="120" align="center">
          <template slot-scope="{ row }">
            <span>{{ calculateDuration(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="progress" label="进度" width="120" align="center">
          <template slot-scope="{ row }">
            <el-progress :percentage="row.progress || 0" :color="getProgressColor(row.progress || 0)" :stroke-width="6"></el-progress>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ getStatusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="start_time" label="开始时间" width="160" align="center" />
        <el-table-column label="操作" width="220" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" icon="el-icon-view" @click="handleView(row)" style="margin-right: 8px;">查看</el-button>
            <el-dropdown trigger="click" @command="handleAction($event, row)">
              <el-button type="text" size="small" icon="el-icon-more">操作<i class="el-icon-arrow-down el-icon--right"></i></el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item v-if="row.status === 1" command="suspend" icon="el-icon-video-pause">挂起</el-dropdown-item>
                <el-dropdown-item v-if="row.status === 4" command="resume" icon="el-icon-video-play">恢复</el-dropdown-item>
                <el-dropdown-item v-if="row.status === 1" command="terminate" icon="el-icon-video-pause">终止</el-dropdown-item>
                <el-dropdown-item command="logs" divided icon="el-icon-document">审批记录</el-dropdown-item>
                <el-dropdown-item command="flowchart" icon="el-icon-share">流程图</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="pagination.page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 实例详情对话框 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="detailDialogVisible"
      width="800px"
      @closed="handleDialogClosed"
    >
      <el-descriptions :column="2" border v-if="currentInstance">
        <el-descriptions-item label="实例编号">{{ currentInstance.instance_no }}</el-descriptions-item>
        <el-descriptions-item label="工单编号">{{ currentInstance.order_no }}</el-descriptions-item>
        <el-descriptions-item label="流程名称">{{ currentInstance.definition_name }}</el-descriptions-item>
        <el-descriptions-item label="发起人">{{ currentInstance.start_user_name }}</el-descriptions-item>
        <el-descriptions-item label="当前节点">{{ currentInstance.current_node_name }}</el-descriptions-item>
        <el-descriptions-item label="状态">{{ getStatusText(currentInstance.status) }}</el-descriptions-item>
      </el-descriptions>

      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
        <el-button
          v-if="currentInstance && currentInstance.status === 1"
          type="warning"
          @click="handleTerminate"
        >
          终止流程
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  apiProcessInstanceList,
  apiProcessInstanceDetail,
  apiProcessInstanceTerminate,
  apiProcessInstanceSuspend,
  apiProcessInstanceNodeLog
} from '@/api/service'

export default {
  name: 'ProcessInstance',

  data () {
    return {
      searchForm: {
        order_no: '',
        status: ''
      },

      loading: false,
      tableData: [],

      pagination: {
        page: 1,
        size: 20,
        total: 0
      },

      detailDialogVisible: false,
      dialogTitle: '',
      activeTab: 'basic',
      currentInstance: null,
      nodeLogs: [],
      variables: []
    }
  },

  mounted () {
    this.loadTableData()
  },

  methods: {
    // 加载表格数据
    async loadTableData () {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          page: this.pagination.page,
          size: this.pagination.size
        }

        if (params.date_range && params.date_range.length === 2) {
          params.start_time = Math.floor(params.date_range[0] / 1000)
          params.end_time = Math.floor(params.date_range[1] / 1000)
          delete params.date_range
        }

        const res = await apiProcessInstanceList(params)
        this.tableData = res.lists || []
        this.pagination.total = res.count || 0
      } catch (error) {
        console.error('加载数据失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 获取状态类型
    getStatusType (status) {
      const types = {
        1: 'primary', // 进行中
        2: 'success', // 已完成
        3: 'danger', // 已终止
        4: 'warning' // 已挂起
      }
      return types[status] || 'info'
    },

    // 获取状态文本
    getStatusText (status) {
      const texts = {
        1: '进行中',
        2: '已完成',
        3: '已终止',
        4: '已挂起'
      }
      return texts[status] || '未知'
    },

    // 获取审批状态文本
    getApprovalStatusText (status) {
      const texts = {
        1: '待处理',
        2: '已通过',
        3: '已驳回',
        4: '已转交',
        5: '已撤回'
      }
      return texts[status] || '未知'
    },

    // 计算耗时
    calculateDuration (row) {
      if (!row.start_time) return '-'

      const start = row.start_time * 1000
      const end = row.end_time ? row.end_time * 1000 : Date.now()
      const duration = end - start

      if (duration < 60000) {
        return `${Math.floor(duration / 1000)}秒`
      } else if (duration < 3600000) {
        return `${Math.floor(duration / 60000)}分钟`
      } else if (duration < 86400000) {
        return `${Math.floor(duration / 3600000)}小时`
      } else {
        return `${Math.floor(duration / 86400000)}天`
      }
    },

    // 搜索
    handleSearch () {
      this.pagination.page = 1
      this.loadTableData()
    },

    // 重置
    handleReset () {
      this.searchForm = {
        order_no: '',
        process_name: '',
        status: '',
        date_range: []
      }
      this.pagination.page = 1
      this.loadTableData()
    },

    // 查看详情
    async handleView (row) {
      this.currentInstance = row
      this.dialogTitle = `流程实例 - ${row.instance_no}`
      this.detailDialogVisible = true

      // 加载详细信息
      try {
        const { data } = await apiProcessInstanceDetail({ id: row.id })
        this.currentInstance = { ...row, ...data }

        // 加载节点记录
        if (row.id) {
          const logData = await apiProcessInstanceNodeLog({ instance_id: row.id })
          this.nodeLogs = logData.data.list || []
        }

        // 解析流程变量
        if (data.variables) {
          try {
            const vars = JSON.parse(data.variables)
            this.variables = Object.entries(vars).map(([key, value]) => ({
              key,
              value: typeof value === 'object' ? JSON.stringify(value) : String(value),
              type: typeof value
            }))
          } catch {
            this.variables = []
          }
        }
      } catch (error) {
        this.$message.error('加载详情失败')
      }
    },

    // 操作
    handleAction (command, row) {
      switch (command) {
        case 'suspend':
          this.handleSuspend(row)
          break
        case 'resume':
          this.handleResume(row)
          break
        case 'terminate':
          this.handleTerminateConfirm(row)
          break
        case 'logs':
          this.$router.push({
            path: '/work_order/process/approval',
            query: { instance_id: row.id }
          })
          break
        case 'flowchart':
          this.handleViewFlowchart(row)
          break
      }
    },

    // 挂起流程
    async handleSuspend (row) {
      try {
        await this.$confirm('确定挂起该流程吗？', '提示', {
          type: 'warning'
        })

        // 模拟挂起操作
        this.$message.success('流程已挂起')
        this.loadTableData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('操作失败')
        }
      }
    },

    // 恢复流程
    async handleResume (row) {
      try {
        await this.$confirm('确定恢复该流程吗？', '提示', {
          type: 'warning'
        })

        // 模拟恢复操作
        this.$message.success('流程已恢复')
        this.loadTableData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('操作失败')
        }
      }
    },

    // 终止流程确认
    handleTerminateConfirm (row) {
      this.$confirm('确定终止该流程吗？终止后将无法恢复', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.handleTerminate(row.id)
      }).catch(() => {})
    },

    // 终止流程
    async handleTerminate (instanceId) {
      try {
        const id = typeof instanceId === 'object' ? this.currentInstance.id : instanceId

        await apiProcessInstanceTerminate({ id })
        this.$message.success('流程已终止')

        if (this.detailDialogVisible) {
          this.detailDialogVisible = false
        }

        this.loadTableData()
      } catch (error) {
        this.$message.error('终止失败')
      }
    },

    // 查看流程图
    handleViewFlowchart (row) {
      this.$router.push({
        path: '/work_order/process/design',
        query: { id: row.definition_id, view: 1, highlight: row.current_node_id }
      })
    },

    // 查看工单
    handleViewOrder (row) {
      // 跳转到工单详情页
      this.$router.push({
        path: '/work_order/detail',
        query: { id: row.order_id }
      })
    },

    // 获取节点标签类型
    getNodeTagType (nodeType) {
      const types = {
        1: 'success', // 开始节点
        2: 'primary', // 处理节点
        3: 'warning', // 条件节点
        4: 'danger', // 结束节点
        5: 'info' // 其他节点
      }
      return types[nodeType] || 'info'
    },

    // 获取进度条颜色
    getProgressColor (percentage) {
      if (percentage < 30) {
        return '#f56c6c'
      } else if (percentage < 70) {
        return '#e6a23c'
      } else {
        return '#67c23a'
      }
    },

    // 对话框关闭
    handleDialogClosed () {
      this.currentInstance = null
      this.nodeLogs = []
      this.variables = []
      this.activeTab = 'basic'
    },

    // 分页大小改变
    handleSizeChange (size) {
      this.pagination.size = size
      this.loadTableData()
    },

    // 页码改变
    handleCurrentChange (page) {
      this.pagination.page = page
      this.loadTableData()
    }
  }
}
</script>

<style lang="scss" scoped>
.process-instance-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);

  .search-container {
    margin-bottom: 20px;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }

  .table-container {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    .process-info {
      display: flex;
      flex-direction: column;
      align-items: center;

      .process-version {
        font-size: 12px;
        color: #909399;
        margin-top: 4px;
      }
    }

    .attachments {
      margin-top: 8px;

      .attachment-link {
        margin-right: 10px;
        color: #409eff;
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .pagination-container {
      padding: 20px;
      text-align: right;
    }
  }

  ::v-deep .el-descriptions__label {
    width: 100px;
  }
}
</style>
